import authService from "../services/auth.service";
import {
  clearAuthenticationStorage,
  getAuthToken,
  normalizeAuthToken,
} from "../utils/authStorage";
import { toogleErrorMessage, toogleSuccessMessage } from "./alertMessage";
import {
  AUTH_ME,
  IS_LOGIN,
  PLAN_LIST,
  TOGGLE_THEME,
  CLIENT_AUTH_LOADING,
  CLIENT_AUTH_ERROR,
  VENDOR_AUTH_LOADING,
  VENDOR_AUTH_ERROR,
  VENDOR_SIGNUP_LOADING,
  VENDOR_SIGNUP_ERROR,
  VENDOR_FORGOT_PASSWORD_LOADING,
  VENDOR_FORGOT_PASSWORD_ERROR,
  VENDOR_RESET_PASSWORD_LOADING,
  VENDOR_RESET_PASSWORD_ERROR,
  DESTROY_SESSION,
} from "./type";

const getResponseMessage = (resp) =>
  resp?.data?.message ||
  resp?.data?.data?.message ||
  resp?.response?.data?.message ||
  resp?.response?.data?.data?.message ||
  "";

export const toggleTheme = (type) => (dispatch) => {
  dispatch({ type: TOGGLE_THEME, payload: type });
  localStorage.setItem("defaultTheme", type);
};

export const loginAction = (payload) => async (dispatch) => {
  return await authService.login(payload).then((resp) => {
    if (resp?.status === 200) {
      dispatch({ type: IS_LOGIN, payload: true });
      localStorage.setItem("authToken", normalizeAuthToken(resp?.data?.token));
    } else {
      toogleErrorMessage(
        resp?.response?.data?.message || "Something went wrong.",
      );
    }
    return resp;
  });
};

export const clientLoginAction = (payload) => async (dispatch) => {
  dispatch({ type: CLIENT_AUTH_LOADING, payload: true });
  const resp = await authService.clientLogin(payload);
  if (resp?.status >= 200 && resp?.status < 300) {
    dispatch({ type: IS_LOGIN, payload: true });
    dispatch({ type: CLIENT_AUTH_ERROR, payload: "" });
    const token = resp?.data?.token || resp?.data?.data?.token;
    if (token) localStorage.setItem("authToken", normalizeAuthToken(token));
    const message = getResponseMessage(resp);
    if (message) toogleSuccessMessage(message);
  } else {
    const message = getResponseMessage(resp);
    dispatch({ type: CLIENT_AUTH_ERROR, payload: message || "" });
    if (message) toogleErrorMessage(message);
  }
  dispatch({ type: CLIENT_AUTH_LOADING, payload: false });
  return resp;
};

export const clearAuthSessionAction = () => (dispatch) => {
  clearAuthenticationStorage();
  dispatch({ type: DESTROY_SESSION });
};

const getTokenVerificationStatus = (response) =>
  response?.status ?? response?.response?.status;

const getTokenVerificationBody = (response) =>
  response?.response?.data?.data ||
  response?.response?.data ||
  response?.data?.data ||
  response?.data ||
  {};

const hasTokenFailureMessage = (response) => {
  const body = getTokenVerificationBody(response);
  const message = String(
    getResponseMessage(response) || body?.error || body?.reason || "",
  );
  return /(?:invalid|expired|missing|required|revoked).{0,32}(?:token|jwt|session)|(?:token|jwt|session).{0,32}(?:invalid|expired|missing|required|revoked)|jwt expired/i.test(
    message,
  );
};

const isExplicitlyInvalidVerification = (response) => {
  const status = getTokenVerificationStatus(response);
  if (status >= 500) return false;
  const body = getTokenVerificationBody(response);
  return (
    body?.valid === false ||
    body?.verified === false ||
    body?.isValid === false ||
    body?.authenticated === false ||
    (body?.success === false && hasTokenFailureMessage(response))
  );
};

const isSuccessfulTokenVerification = (response) => {
  const status = getTokenVerificationStatus(response);
  return (
    status >= 200 &&
    status < 300 &&
    getTokenVerificationBody(response)?.success !== false &&
    !isExplicitlyInvalidVerification(response)
  );
};

const isInvalidTokenVerification = (response) => {
  const status = getTokenVerificationStatus(response);
  return (
    status === 401 ||
    isExplicitlyInvalidVerification(response) ||
    (status >= 400 && status < 500 && hasTokenFailureMessage(response))
  );
};

const clearSessionIfTokenIsCurrent = (dispatch, token) => {
  if (getAuthToken() === token) dispatch(clearAuthSessionAction());
};

export const verifyVendorTokenAction = () => async (dispatch) => {
  const token = getAuthToken();
  if (!token) {
    dispatch(clearAuthSessionAction());
    return { verified: false, invalidToken: true, status: 401 };
  }

  try {
    const response = await authService.verifyVendorToken(token);
    if (isSuccessfulTokenVerification(response)) {
      dispatch({ type: IS_LOGIN, payload: true });
      return { response, verified: true };
    }

    const invalidToken = isInvalidTokenVerification(response);
    if (invalidToken) clearSessionIfTokenIsCurrent(dispatch, token);
    return {
      response,
      verified: false,
      invalidToken,
      unavailable: !invalidToken,
    };
  } catch (error) {
    const invalidToken = isInvalidTokenVerification(error);
    if (invalidToken) clearSessionIfTokenIsCurrent(dispatch, token);
    return {
      response: error,
      verified: false,
      invalidToken,
      unavailable: !invalidToken,
    };
  }
};
export const verifyTokenAction = () => async (dispatch) => {
  const token = getAuthToken();
  if (!token) {
    dispatch(clearAuthSessionAction());
    return { status: 401 };
  }
  const response = await authService.verifyToken(token);
  if (isSuccessfulTokenVerification(response)) {
    dispatch({ type: IS_LOGIN, payload: true });
  } else if (isInvalidTokenVerification(response)) {
    clearSessionIfTokenIsCurrent(dispatch, token);
  }
  return response;
};
export const singUpAction = async (payload) => {
  return await authService.signUp(payload).then((resp) => {
    if (resp.status === 200) {
      toogleSuccessMessage(
        "Success! Your account has been created. Log in to get started",
      );
    } else {
      toogleErrorMessage(resp.response.data.message || "Something went wrong.");
    }
    return resp;
  });
};

export const clientSignUpAction = (payload) => async (dispatch) => {
  dispatch({ type: CLIENT_AUTH_LOADING, payload: true });
  const resp = await authService.clientSignUp(payload);
  if (resp?.status >= 200 && resp?.status < 300) {
    dispatch({ type: CLIENT_AUTH_ERROR, payload: "" });
    const message = getResponseMessage(resp);
    if (message) toogleSuccessMessage(message);
  } else {
    const message = getResponseMessage(resp);
    dispatch({ type: CLIENT_AUTH_ERROR, payload: message || "" });
    if (message) toogleErrorMessage(message);
  }
  dispatch({ type: CLIENT_AUTH_LOADING, payload: false });
  return resp;
};

export const verifyClientTokenAction = () => async (dispatch) => {
  const token = getAuthToken();
  if (!token) {
    dispatch(clearAuthSessionAction());
    return { status: 401 };
  }

  const response = await authService.verifyClientToken(token);
  if (isSuccessfulTokenVerification(response)) {
    dispatch({ type: IS_LOGIN, payload: true });
  } else if (isInvalidTokenVerification(response)) {
    clearSessionIfTokenIsCurrent(dispatch, token);
  }
  return response;
};
export const vendorLoginAction = (payload) => async (dispatch) => {
  dispatch({ type: VENDOR_AUTH_LOADING, payload: true });

  const resp = await authService.vendorLogin(payload);
  const message = getResponseMessage(resp);

  if (resp?.status >= 200 && resp?.status < 300) {
    const token = resp?.data?.token || resp?.data?.data?.token;
    if (token) localStorage.setItem("authToken", normalizeAuthToken(token));
    dispatch({ type: VENDOR_AUTH_ERROR, payload: "" });
    if (message) toogleSuccessMessage(message);
  } else {
    dispatch({ type: IS_LOGIN, payload: false });
    dispatch({ type: VENDOR_AUTH_ERROR, payload: message || "Invalid email address or password." });
    if (message) toogleErrorMessage(message);
  }

  dispatch({ type: VENDOR_AUTH_LOADING, payload: false });
  return resp;
};

export const vendorSignUpAction = (payload) => async (dispatch) => {
  dispatch({ type: VENDOR_SIGNUP_LOADING, payload: true });
  dispatch({ type: VENDOR_SIGNUP_ERROR, payload: "" });
  try {
    const response = await authService.vendorSignUp(payload);
    const status = response?.status ?? response?.response?.status;
    const message = getResponseMessage(response);

    if (status >= 200 && status < 300 && response?.data?.success !== false && response?.data?.data?.success !== false) {
      if (message) toogleSuccessMessage(message);
      return { success: true, response };
    }

    const error = message || "Unable to create vendor account.";
    dispatch({ type: VENDOR_SIGNUP_ERROR, payload: error });
    toogleErrorMessage(error);
    return { success: false, response, error };
  } catch (error) {
    const message =
      getResponseMessage(error) || error?.message || "Unable to create vendor account.";
    dispatch({ type: VENDOR_SIGNUP_ERROR, payload: message });
    toogleErrorMessage(message);
    return { success: false, error };
  } finally {
    dispatch({ type: VENDOR_SIGNUP_LOADING, payload: false });
  }
};

export const verifyVendorSignupOtpAction = (payload) => async (dispatch) => {
  dispatch({ type: VENDOR_SIGNUP_LOADING, payload: true });
  dispatch({ type: VENDOR_SIGNUP_ERROR, payload: "" });

  try {
    const response = await authService.verifyVendorSignupOtp(payload);
    const status = response?.status ?? response?.response?.status;
    const message = getResponseMessage(response);

    if (status >= 200 && status < 300 && response?.data?.success !== false && response?.data?.data?.success !== false) {
      if (message) toogleSuccessMessage(message);
      return { success: true, response };
    }

    const error = message || "Unable to verify the OTP.";
    dispatch({ type: VENDOR_SIGNUP_ERROR, payload: error });
    toogleErrorMessage(error);
    return { success: false, response, error };
  } catch (error) {
    const message =
      getResponseMessage(error) || error?.message || "Unable to verify the OTP.";
    dispatch({ type: VENDOR_SIGNUP_ERROR, payload: message });
    toogleErrorMessage(message);
    return { success: false, error: message };
  } finally {
    dispatch({ type: VENDOR_SIGNUP_LOADING, payload: false });
  }
};

export const vendorForgotPasswordAction = (payload) => async (dispatch) => {
  dispatch({ type: VENDOR_FORGOT_PASSWORD_LOADING, payload: true });
  dispatch({ type: VENDOR_FORGOT_PASSWORD_ERROR, payload: "" });

  try {
    const response = await authService.vendorForgotPassword(payload);
    const status = response?.status ?? response?.response?.status;
    const message = getResponseMessage(response);

    if (status >= 200 && status < 300) {
      toogleSuccessMessage(message || "Password reset instructions have been sent.");
      return { success: true, response };
    }

    const error = message || "Unable to send password reset instructions.";
    dispatch({ type: VENDOR_FORGOT_PASSWORD_ERROR, payload: error });
    toogleErrorMessage(error);
    return { success: false, response };
  } catch (error) {
    const message = getResponseMessage(error) || error?.message || "Unable to send password reset instructions.";
    dispatch({ type: VENDOR_FORGOT_PASSWORD_ERROR, payload: message });
    toogleErrorMessage(message);
    return { success: false, error };
  } finally {
    dispatch({ type: VENDOR_FORGOT_PASSWORD_LOADING, payload: false });
  }
};

export const vendorResetPasswordAction = (token, payload) => async (dispatch) => {
  dispatch({ type: VENDOR_RESET_PASSWORD_LOADING, payload: true });
  dispatch({ type: VENDOR_RESET_PASSWORD_ERROR, payload: "" });

  try {
    const response = await authService.vendorResetPassword(token, payload);
    const status = response?.status ?? response?.response?.status;
    const message = getResponseMessage(response);

    if (status >= 200 && status < 300) {
      toogleSuccessMessage(message || "Your password has been reset successfully.");
      return { success: true, response };
    }

    const error = message || "Unable to reset your password.";
    dispatch({ type: VENDOR_RESET_PASSWORD_ERROR, payload: error });
    toogleErrorMessage(error);
    return { success: false, response };
  } catch (error) {
    const message = getResponseMessage(error) || error?.message || "Unable to reset your password.";
    dispatch({ type: VENDOR_RESET_PASSWORD_ERROR, payload: message });
    toogleErrorMessage(message);
    return { success: false, error };
  } finally {
    dispatch({ type: VENDOR_RESET_PASSWORD_LOADING, payload: false });
  }
};

export const clientGoogleLoginAction = (payload) => async (dispatch) => {
  dispatch({ type: CLIENT_AUTH_LOADING, payload: true });
  const resp = await authService.clientGoogleLogin(payload);
  const message = getResponseMessage(resp);
  if (resp?.status >= 200 && resp?.status < 300) {
    const token = resp?.data?.token || resp?.data?.data?.token;
    if (token) localStorage.setItem("authToken", normalizeAuthToken(token));
    dispatch({ type: CLIENT_AUTH_ERROR, payload: "" });
    if (message) toogleSuccessMessage(message);
  } else {
    dispatch({ type: IS_LOGIN, payload: false });
    dispatch({ type: CLIENT_AUTH_ERROR, payload: message || "" });
    if (message) toogleErrorMessage(message);
  }
  dispatch({ type: CLIENT_AUTH_LOADING, payload: false });
  return resp;
};

export const getClientProfileAction = () => async (dispatch) => {
  const resp = await authService.clientProfile();
  if (resp?.status >= 200 && resp?.status < 300) {
    dispatch({ type: AUTH_ME, payload: resp?.data?.data || resp?.data });
  }
  return resp;
};

export const updateClientProfileAction = (payload) => async (dispatch) => {
  const resp = await authService.updateClientProfile(payload);
  if (resp?.status >= 200 && resp?.status < 300) {
    dispatch(getClientProfileAction());
  }
  return resp;
};

export const uploadClientImageAction = (file) => async () => {
  const resp = await authService.uploadClientImage(file);
  const message = getResponseMessage(resp);
  if (resp?.status >= 200 && resp?.status < 300) {
    if (message) toogleSuccessMessage(message);
  } else if (message) {
    toogleErrorMessage(message);
  }
  return resp;
};
export const getAuthMeAction = () => (dispatch) => {
  return authService.authMe().then((resp) => {
    if (resp?.status === 200) {
      dispatch({ type: AUTH_ME, payload: resp?.data });
    }
    return resp;
  });
};

export const editAuthAction = (payload) => async (dispatch) => {
  return await authService.editAuthMe(payload).then((resp) => {
    if (resp.status === 200) {
      toogleSuccessMessage("User details updated successfully.");
    } else {
      toogleErrorMessage(resp.data.message || "Something went wrong.");
    }
    return resp;
  });
};
export const getSubscription = () => (dispatch) => {
  return authService.planDetails().then((resp) => {
    if (resp.status === 200) {
      dispatch({ type: PLAN_LIST, payload: resp.data });
    }
    return resp;
  });
};

export const logoutAction = () => (dispatch) => {
  dispatch(clearAuthSessionAction());
};
export const vendorChangePasswordAction = (payload) => async () => {
  const response = await authService.vendorChangePassword(payload);
  const message = getResponseMessage(response);

  if (response?.status >= 200 && response?.status < 300) {
    toogleSuccessMessage(message || "Password updated successfully.");
  } else {
    toogleErrorMessage(message || "Unable to update password.");
  }

  return response;
};


export const getVendorProfileAction = () => async (dispatch) => {
  const response = await authService.vendorProfile();
  const body = response?.data || {};
  const data = body?.data || body;
  const vendor =
    data?.vendor ||
    data?.data?.vendor ||
    body?.vendor ||
    data?.data ||
    data;
  const companyProfile =
    data?.companyProfile ||
    data?.data?.companyProfile ||
    body?.companyProfile ||
    vendor?.companyProfile;
  const profile = companyProfile ? { ...vendor, companyProfile } : vendor;

  if (response?.status >= 200 && response?.status < 300 && profile) {
    dispatch({ type: AUTH_ME, payload: profile });
  } else {
    toogleErrorMessage(getResponseMessage(response) || "Unable to load vendor profile.");
  }

  return response;
};

const getUploadId = (response) => {
  const body = response?.data || {};
  const candidates = [
    body.document?._id,
    body.data?.document?._id,
    body.data?.data?.document?._id,
    body._id,
    body.data?._id,
    body.data?.data?._id,
    body.upload?._id,
    body.data?.upload?._id,
    body.data?.data?.upload?._id,
    body.file?._id,
    body.data?.file?._id,
    body.data?.data?.file?._id,
    body.uploadId,
    body.fileId,
    body.id,
    body._id,
    body.file,
    body.upload,
    body.document,
    body.files?.[0],
    body.data?.uploadId,
    body.data?.fileId,
    body.data?.id,
    body.data?._id,
    body.data?.file,
    body.data?.upload,
    body.data?.document,
    body.data?.files?.[0],
    body.data?.data?.uploadId,
    body.data?.data?.fileId,
    body.data?.data?.id,
    body.data?.data?._id,
    body.data?.data?.file,
    body.data?.data?.upload,
  ];

  return (
    candidates
      .map((candidate) =>
        typeof candidate === "string"
          ? candidate
          : candidate?._id || candidate?.id || "",
      )
      .find(Boolean) || ""
  );
};

const isSuccessfulResponse = (response) => {
  const status = response?.status || response?.response?.status;
  return status >= 200 && status < 300;
};

export const uploadVendorProfileImageAction = (file) => async () => {
  const response = await authService.uploadVendorProfileImage(file);
  const uploadId = getUploadId(response);

  if (isSuccessfulResponse(response) && uploadId) {
    return { success: true, uploadId, response };
  }

  const message =
    getResponseMessage(response) ||
    (isSuccessfulResponse(response)
      ? "The uploaded image did not include an upload ID."
      : "Unable to upload profile image.");
  toogleErrorMessage(message);

  return { success: false, uploadId: "", response, message };
};

export const uploadVendorDocumentAction = (file) => async () => {
  const response = await authService.uploadVendorProfileImage(file);
  const uploadId = getUploadId(response);

  if (isSuccessfulResponse(response) && uploadId) {
    return { success: true, uploadId, response };
  }

  const message =
    getResponseMessage(response) ||
    (isSuccessfulResponse(response)
      ? "The uploaded document did not include an upload ID."
      : "Unable to upload document.");
  toogleErrorMessage(message);

  return { success: false, uploadId: "", response, message };
};
export const updateVendorProfileAction = (payload) => async (dispatch) => {
  const response = await authService.updateVendorProfile(payload);

  if (isSuccessfulResponse(response)) {
    toogleSuccessMessage(
      getResponseMessage(response) || "Profile updated successfully.",
    );
    await dispatch(getVendorProfileAction());
  } else {
    toogleErrorMessage(
      getResponseMessage(response) || "Unable to update vendor profile.",
    );
  }

  return response;
};

export const completeVendorProfileAction = (payload) => async () => {
  const response = await authService.completeVendorProfile(payload);
  const status = response?.status ?? response?.response?.status;
  const body = response?.data || response?.response?.data || {};
  const data = body?.data?.data || body?.data || body;
  const succeeded =
    status >= 200 &&
    status < 300 &&
    body.success !== false &&
    body.data?.success !== false &&
    body.data?.data?.success !== false &&
    data?.success !== false &&
    data?.verified !== false;
  const message = getResponseMessage(response);

  if (succeeded) {
    return { success: true, response, status, data };
  }

  const error = message || "Unable to complete vendor profile.";
  toogleErrorMessage(error);
  return { success: false, response, status, data, error };
};

export const previewVendorProfileAction = (payload) => async () => {
  const response = await authService.previewVendorProfile(payload);
  const status = response?.status ?? response?.response?.status;
  const body = response?.data || response?.response?.data || {};
  const data = body?.data?.data || body?.data || body;
  const succeeded =
    status >= 200 &&
    status < 300 &&
    body.success !== false &&
    body.data?.success !== false &&
    body.data?.data?.success !== false &&
    data?.success !== false;
  const error =
    getResponseMessage(response) ||
    body?.error ||
    data?.error ||
    "Unable to extract information from vendor documents.";

  return { success: succeeded, response, status, data, error };
};
